import { Box } from '@primer/components';
import React, {
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactDom from 'react-dom';
import Toast from 'uikit/alerts/Toast/Toast';

import './toast.scss';

export const ToastContainer = ({ toasts = [], handleClose }) =>
    ReactDom.createPortal(
        <>
            {toasts.map((toast, index) => {
                const { id, variant, content } = toast;
                return (
                    <Box
                        key={id}
                        id={`toast-item-${index}`}
                        className="toast-provider animated fadeInDown"
                    >
                        <Toast
                            variant={variant}
                            handleCancel={() => handleClose(id)}
                        >
                            {content}
                        </Toast>
                    </Box>
                );
            })}
        </>,
        document.getElementById('toast-portal')
    );

export const ToastContext = createContext();

const TOAST_PENDING_QUEUE = [];
const TOAST_ALLOWED_LENGTH = 10;

const ToastController = forwardRef((_, ref) => {
    const [toastList, setToastList] = useState([]);
    const [isDisableHideInterval, setIsDisableHideInterval] = useState(false);

    const addToastFromPendingQueue = useCallback(() => {
        const lastToastPendingItem = TOAST_PENDING_QUEUE.pop();
        if (!lastToastPendingItem) return;

        setToastList((prevList) => {
            if (prevList.length >= TOAST_ALLOWED_LENGTH) {
                TOAST_PENDING_QUEUE.push(lastToastPendingItem);
                return prevList;
            }

            return [...prevList, lastToastPendingItem];
        });
    }, []);

    const handleClose = useCallback(
        (id) => {
            setToastList((prevList) => prevList.filter((t) => t.id !== id));
            addToastFromPendingQueue();
        },
        [addToastFromPendingQueue]
    );

    useImperativeHandle(
        ref,
        () => ({
            addToast: (
                content = '',
                variant = 'success',
                disableHideInterval = false
            ) => {
                setIsDisableHideInterval(disableHideInterval);

                const payload = {
                    id: new Date().getTime(),
                    content,
                    variant,
                };

                setToastList((prevList) => {
                    if (prevList.length < TOAST_ALLOWED_LENGTH) {
                        return [...prevList, payload];
                    }
                    TOAST_PENDING_QUEUE.push(payload);
                    return prevList;
                });

                return payload;
            },
            handleClose,
        }),
        [handleClose]
    );

    useEffect(() => {
        if (isDisableHideInterval) return;
        if (toastList.length > 0) {
            const timer = setTimeout(() => {
                setToastList((prev) => prev.slice(1));
                addToastFromPendingQueue();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [toastList, isDisableHideInterval, addToastFromPendingQueue]);

    return <ToastContainer toasts={toastList} handleClose={handleClose} />;
});

ToastController.displayName = 'ToastController';

export const ToastProvider = ({ children }) => {
    const toastControllerRef = useRef(null);

    const showToast = useCallback(
        (content = '', variant = 'success', disableHideInterval = false) =>
            toastControllerRef.current?.addToast(
                content,
                variant,
                disableHideInterval
            ),
        []
    );

    const handleClose = useCallback(
        (id) => toastControllerRef.current?.handleClose?.(id),
        []
    );

    const contextValue = useMemo(
        () => ({ showToast, handleClose }),
        [showToast, handleClose]
    );

    return (
        <>
            <ToastContext.Provider value={contextValue}>
                {children}
            </ToastContext.Provider>
            <ToastController ref={toastControllerRef} />
        </>
    );
};

export const useToast = () => {
    const toastHelpers = useContext(ToastContext);
    return toastHelpers;
};
